<template>
  <section class="lamella-section">
    <button class="lamella-section__toggle" :aria-expanded="isOpen" @click="handleToggle">
      <span class="lamella-section__toggle-label">
        <span class="lamella-section__chevron" :class="{ 'lamella-section__chevron--open': isOpen }">▸</span>
        <span>Lamellae</span>
      </span>
      <span>{{ summary.total }}</span>
    </button>
    <div v-if="isOpen" class="lamella-section__body">
      <div class="lamella-section__hero">
        <div class="lamella-section__metrics">
          <div class="lamella-section__metric">
            <strong>{{ summary.total }}</strong>
            <span>Total</span>
          </div>
          <div class="lamella-section__metric">
            <strong>{{ summary.active }}</strong>
            <span>Active</span>
          </div>
          <div class="lamella-section__metric">
            <strong>{{ summary.completed }}</strong>
            <span>Done</span>
          </div>
          <div class="lamella-section__metric">
            <strong>{{ summary.alert }}</strong>
            <span>Alert</span>
          </div>
        </div>
        <div class="lamella-section__actions">
          <button type="button" class="lamella-section__archive" @click="showArchiveBrowser = true">
            Browse archived grids
          </button>
        </div>
      </div>
      <div v-if="lamellae.length" class="lamella-section__list">
        <LamellaCard
          v-for="lamella in lamellae"
          :key="lamella.id"
          :lamella="lamella"
          :readonly="readonly"
          @delete="handleDelete"
          @open="handleOpen"
        />
      </div>
      <div v-else class="lamella-section__empty">No lamellae linked to this grid.</div>
      <form v-if="!readonly" class="lamella-section__form" @submit.prevent="handleCreate">
        <div class="lamella-section__form-header">
          <div>
            <p class="lamella-section__eyebrow">New Lamella</p>
            <h3 class="lamella-section__form-title">Attach a tracking record to this grid</h3>
          </div>
          <span class="lamella-section__grid-chip">{{ gridName || "Active grid" }}</span>
        </div>
        <div class="lamella-section__form-grid">
          <label class="lamella-section__field lamella-section__field--wide">
            <span>Name</span>
            <input v-model="draft.name" type="text" placeholder="Lamella 03, edge candidate, center strip..." />
          </label>
          <label class="lamella-section__field">
            <span>Current Stage</span>
            <input v-model="draft.currentStage" type="text" placeholder="Rough mill, polish, liftout..." />
          </label>
          <label class="lamella-section__field">
            <span>Status</span>
            <select v-model="draft.status">
              <option value="pending">Pending review</option>
              <option value="milling">Milling in progress</option>
              <option value="liftout">Liftout</option>
              <option value="completed">Completed</option>
            </select>
          </label>
        </div>
        <div class="lamella-section__form-footer">
          <p class="lamella-section__form-hint">Use a short name operators will recognize during screening and milling.</p>
          <button type="submit" class="lamella-section__submit">Add Lamella</button>
        </div>
      </form>
    </div>
  </section>
  <ArchivedGridBrowser
    :open="showArchiveBrowser"
    @close="showArchiveBrowser = false"
    @select-lamella="handleArchiveSelection"
  />
  <LamellaDetailModal
    :open="Boolean(selectedLamella)"
    :lamella="selectedLamella"
    :grid-name="gridName"
    :readonly="readonly"
    @close="selectedLamellaId = null"
  />
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { useInventoryStore } from "../../../../stores/inventory";
import ArchivedGridBrowser from "./ArchivedGridBrowser.vue";
import LamellaCard from "./LamellaCard.vue";
import LamellaDetailModal from "./LamellaDetailModal.vue";

const props = defineProps({
  gridId: {
    type: String,
    required: true,
  },
  gridName: {
    type: String,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  autoExpand: {
    type: Boolean,
    default: false,
  },
});

const inventoryStore = useInventoryStore();
const isOpen = ref(props.autoExpand);
const showArchiveBrowser = ref(false);
const selectedLamellaId = ref(null);
const draft = reactive({
  name: "",
  currentStage: "",
  status: "pending",
});

const lamellae = computed(() => inventoryStore.getLamellaeForGrid(props.gridId));
const summary = computed(() => inventoryStore.getLamellaSummaryForGrid(props.gridId));
const selectedLamella = computed(() =>
  selectedLamellaId.value ? inventoryStore.getLamella(selectedLamellaId.value) : null
);

async function handleCreate() {
  if (!draft.name.trim()) return;
  try {
    await inventoryStore.createLamella({
      gridId: props.gridId,
      name: draft.name.trim(),
      currentStage: draft.currentStage.trim() || null,
      status: draft.status,
    });
    draft.name = "";
    draft.currentStage = "";
    draft.status = "pending";
    isOpen.value = true;
  } catch (error) {
    window.alert(error?.message || "Failed to create lamella");
  }
}

async function handleDelete(id) {
  const confirmed = window.confirm("Delete this lamella record?");
  if (!confirmed) return;
  try {
    await inventoryStore.deleteLamella(id);
  } catch (error) {
    window.alert(error?.message || "Failed to delete lamella");
  }
}

function handleOpen(id) {
  selectedLamellaId.value = id;
}

function handleArchiveSelection(id) {
  showArchiveBrowser.value = false;
  selectedLamellaId.value = id;
}

function handleToggle() {
  isOpen.value = !isOpen.value;
}
</script>

<style scoped>
.lamella-section {
  margin-top: 0.75rem;
  border-top: 1px dashed rgba(14, 165, 233, 0.4);
  padding-top: 0.75rem;
}
.lamella-section__toggle {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: none;
  background: #eef2f7;
  border-radius: 0.75rem;
  padding: 0.65rem 0.8rem;
  font-weight: 700;
  cursor: pointer;
}
.lamella-section__toggle-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.lamella-section__chevron {
  display: inline-flex;
  transform: rotate(0deg);
  transition: transform 0.2s ease;
  color: #475569;
  margin-top: -0.05rem;
}
.lamella-section__chevron--open {
  transform: rotate(90deg);
}
.lamella-section__hero {
  display: flex;
  justify-content: space-between;
  gap: 0.9rem;
  align-items: center;
  margin-bottom: 0.85rem;
  padding: 0.95rem;
  border-radius: 1rem;
  background:
    linear-gradient(135deg, rgba(14, 165, 233, 0.14), rgba(15, 118, 110, 0.08)),
    #f8fafc;
}
.lamella-section__metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
  flex: 1;
}
.lamella-section__metric {
  background: rgba(255, 255, 255, 0.88);
  border-radius: 0.9rem;
  padding: 0.75rem;
}
.lamella-section__metric strong,
.lamella-section__metric span {
  display: block;
}
.lamella-section__metric strong {
  font-size: 1.2rem;
  color: #0f172a;
}
.lamella-section__metric span {
  color: #64748b;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.lamella-section__archive {
  border: 1px solid rgba(217, 119, 6, 0.35);
  border-radius: 999px;
  background: #fff7ed;
  color: #9a3412;
  font-weight: 700;
  padding: 0.55rem 0.9rem;
}
.lamella-section__body { margin-top: 0.75rem; }
.lamella-section__list { display: grid; gap: 0.6rem; }
.lamella-section__empty { color: #6b7280; font-size: 0.82rem; }
.lamella-section__form {
  margin-top: 0.95rem;
  display: grid;
  gap: 0.85rem;
  padding: 1rem;
  border-radius: 1rem;
  border: 1px solid rgba(14, 165, 233, 0.14);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(241, 245, 249, 0.9)),
    #fff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}
.lamella-section__form-header,
.lamella-section__form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.lamella-section__eyebrow {
  margin: 0 0 0.2rem;
  font-size: 0.68rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #0f766e;
  font-weight: 800;
}
.lamella-section__form-title {
  margin: 0;
  font-size: 0.96rem;
  line-height: 1.2;
  color: #0f172a;
}
.lamella-section__grid-chip {
  padding: 0.42rem 0.7rem;
  border-radius: 999px;
  background: #e0f2fe;
  color: #075985;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}
.lamella-section__form-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.lamella-section__field {
  display: grid;
  gap: 0.35rem;
}
.lamella-section__field--wide {
  grid-column: 1 / -1;
}
.lamella-section__field span {
  font-size: 0.73rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 700;
}
.lamella-section__field input,
.lamella-section__field select {
  width: 100%;
  min-height: 2.8rem;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.98);
  padding: 0.75rem 0.9rem;
  color: #0f172a;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.lamella-section__field input::placeholder {
  color: #94a3b8;
}
.lamella-section__field input:focus,
.lamella-section__field select:focus {
  outline: none;
  border-color: rgba(14, 165, 233, 0.7);
  box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.12);
}
.lamella-section__form-hint {
  margin: 0;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.4;
}
.lamella-section__submit {
  border: none;
  border-radius: 999px;
  padding: 0.78rem 1.1rem;
  background: linear-gradient(135deg, #0f766e, #0369a1);
  color: #fff;
  font-weight: 800;
  letter-spacing: 0.04em;
  box-shadow: 0 10px 20px rgba(8, 47, 73, 0.16);
}
@media (max-width: 800px) {
  .lamella-section__hero {
    flex-direction: column;
    align-items: stretch;
  }
  .lamella-section__metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .lamella-section__form-header,
  .lamella-section__form-footer {
    flex-direction: column;
    align-items: stretch;
  }
  .lamella-section__form-grid {
    grid-template-columns: 1fr;
  }
  .lamella-section__field--wide {
    grid-column: auto;
  }
}
</style>
